function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React from "react";
import { Link } from "react-router-dom";
import { CssContainer } from "../../components/container";
import { PageHeader, Switcher, Pill } from "../../components/analytics-kit";
import account from "../../data/account";
import { hasTenantFeature } from "../../functions/tenant-features";
import { resolveBusinessModel } from "../sales/territory-common";
import { canViewQuotes } from "../sales/sales-quotes-api";

/*
 * Shared pieces for the quotes and order desk (SALES_QUOTES_ORDER_DESK.md §7):
 * the page shell (kit header + Quotes | Orders switcher), status vocabulary,
 * money and date formatting, and the feature / role gate.
 */

export const QUOTE_STATUSES = [["draft", "Draft", "neutral"], ["pending_approval", "Pending approval", "warning"], ["sent", "Sent", "info"], ["viewed", "Viewed", "info"], ["converted", "Accepted", "success"], ["declined", "Declined", "danger"], ["expired", "Expired", "warning"], ["void", "Void", "neutral"]];
export const OPEN_STATUSES = ["draft", "pending_approval", "sent", "viewed"];
// The server never writes `accepted`: acceptance goes straight to `converted` with the order request set. One state in chips and filters.
export const normStatus = s => String(s || "").toLowerCase() === "accepted" ? "converted" : String(s || "").toLowerCase();
export const quoteStatusLabel = s => {
  const hit = QUOTE_STATUSES.find(([k]) => k === normStatus(s));
  return hit ? hit[1] : String(s || "").replace(/_/g, " ");
};
export const quoteStatusTone = s => {
  const hit = QUOTE_STATUSES.find(([k]) => k === normStatus(s));
  return hit ? hit[2] : "neutral";
};
export const isEditable = q => !!q && ["draft", "pending_approval", "sent", "viewed", "expired"].includes(q.status);
export const isLocked = q => !!q && ["converted", "void", "accepted", "declined"].includes(q.status);
export function money(v, dashZero = false) {
  const x = Number(v);
  if (!Number.isFinite(x) || dashZero && x === 0) return "—";
  return x.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
export function money0(v) {
  const x = Number(v);
  if (!Number.isFinite(x)) return "—";
  return x.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  });
}
export function pctFmt(v) {
  const x = Number(v);
  return Number.isFinite(x) ? `${Math.round(x * 100) / 100}%` : "—";
}
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function fmtDate(v, withYear = true) {
  if (!v) return "";
  const s = String(v);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(s + "T00:00:00") : new Date(s);
  if (isNaN(d.getTime())) return s;
  return `${MON[d.getMonth()]} ${d.getDate()}${withYear ? `, ${d.getFullYear()}` : ""}`;
}
export function fmtDateTime(v) {
  if (!v) return "";
  const d = new Date(String(v));
  if (isNaN(d.getTime())) return String(v);
  let h = d.getHours();
  const ap = h >= 12 ? "pm" : "am";
  h = h % 12 || 12;
  return `${MON[d.getMonth()]} ${d.getDate()} ${h}:${String(d.getMinutes()).padStart(2, "0")}${ap}`;
}
export function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d - today) / 86400000);
}
export function addDays(days) {
  const d = new Date();
  d.setDate(d.getDate() + (Number(days) || 0));
  return d.toISOString().slice(0, 10);
}
export function businessDaysSince(v) {
  if (!v) return 0;
  const start = new Date(String(v));
  if (isNaN(start.getTime())) return 0;
  const end = new Date();
  let count = 0;
  const d = new Date(start);
  d.setHours(0, 0, 0, 0);
  while (d < end) {
    d.setDate(d.getDate() + 1);
    const wd = d.getDay();
    if (wd !== 0 && wd !== 6) count += 1;
  }
  return count;
}
export function relTime(v) {
  if (!v) return "";
  const d = new Date(String(v));
  if (isNaN(d.getTime())) return "";
  const m = Math.round((Date.now() - d.getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr ago`;
  return fmtDateTime(v);
}

/** Status pill for a quote; sent/viewed quotes within 7 days of validity read "Expiring". */
export function QuoteStatus({
  q
}) {
  if (!q) return null;
  const left = daysUntil(q.validUntil);
  if (["sent", "viewed"].includes(q.status) && left != null && left <= 7 && left >= 0) return /*#__PURE__*/React.createElement(Pill, {
    tone: "warning"
  }, "Expiring");
  return /*#__PURE__*/React.createElement(Pill, {
    tone: quoteStatusTone(q.status)
  }, quoteStatusLabel(q.status));
}
export const Btn = ({
  primary,
  danger,
  flat,
  small,
  children,
  ...rest
}) => /*#__PURE__*/React.createElement("button", _extends({
  type: "button"
}, rest, {
  className: `k-button k-button-${small ? "sm" : "md"} k-rounded-md ${flat ? "k-button-flat k-button-flat-base" : `k-button-solid k-button-solid-${primary ? "primary" : danger ? "error" : "base"}`}${rest.className ? " " + rest.className : ""}`
}), children);

/** One-line notice (tone = info | warning | danger). */
export function Notice({
  tone,
  children,
  onClose
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: `wa-notice${tone && tone !== "info" ? " " + tone : ""}`
  }, /*#__PURE__*/React.createElement("span", {
    className: "grow"
  }, children), onClose ? /*#__PURE__*/React.createElement("a", {
    className: "dismiss",
    onClick: onClose
  }, "dismiss") : null);
}
export function errText(e, fallback) {
  if (!e) return fallback;
  const d = e.data || {};
  const code = d.code || d.error || "";
  const map = {
    not_editable: "This quote is converted or void and cannot be edited.",
    invalid_configuration: `A configuration is not allowed by the catalog${d.option ? ` (${d.option})` : ""}.`,
    override_reason_required: "An override needs a reason.",
    override_exceeds_limit: "That override is beyond the tier's limit; the quote will wait for an administrator's approval before send.",
    studio_not_enabled: "Studio is not enabled for this workspace, so a COM request cannot be created. Add a fabric note on the line instead.",
    pending_approval: "This quote is waiting for an administrator's approval before it can be sent.",
    comcol_incomplete: "A COM/COL line still needs its fabric before this quote can be sent.",
    no_contact: "Add a contact email before sending.",
    product_not_found: "That product is not in the catalog any more.",
    invalid_quantity: "Quantity must be a whole number of 1 or more.",
    too_many_lines: "Too many lines on one quote; split it into two quotes."
  };
  if (map[code]) return map[code];
  return d.message && String(d.message) || typeof d.error === "string" && d.error || e.message || fallback;
}

/** Copy to the clipboard without a dialog; returns a promise resolving to true on success. */
export function copyText(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(text).then(() => true).catch(() => false);
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return Promise.resolve(!!ok);
  } catch (e) {
    return Promise.resolve(false);
  }
}
export function companyName() {
  return String((account.get() || {}).company || "");
}

/** Feature + role gate. Renders the calm module-empty line when the desk does not apply. */
export class QuotesGate extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      bm: null
    };
    this._alive = true;
  }
  componentDidMount() {
    resolveBusinessModel().then(bm => this._alive && this.setState({
      bm
    })).catch(() => this._alive && this.setState({
      bm: "both"
    }));
  }
  componentWillUnmount() {
    this._alive = false;
  }
  render() {
    const {
      bm
    } = this.state;
    if (bm === null) return null;
    if (!hasTenantFeature("quotes") || bm === "retail") {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "The quotes and order desk is not configured for this workspace. It applies to manufacturer workspaces with the quotes feature."));
    }
    if (!canViewQuotes()) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Quotes are available to owners, administrators and sales users. Ask an administrator to add you to the sales group."));
    }
    return this.props.children;
  }
}
const TABS = [{
  to: "/Quotes",
  label: "Quotes",
  isActive: p => p === "/Quotes" || p.startsWith("/Quotes/") && !p.startsWith("/Quotes/orders")
}, {
  to: "/Quotes/orders",
  label: "Orders",
  isActive: p => p.startsWith("/Quotes/orders")
}];
export function QuotesShell({
  title = "Quotes",
  crumbs,
  subtitle,
  chips,
  actions,
  children,
  cls = ""
}) {
  return /*#__PURE__*/React.createElement("div", {
    className: `territory-view wa-view qt-view ${cls}`
  }, /*#__PURE__*/React.createElement(PageHeader, {
    crumbs: crumbs,
    title: title,
    subtitle: subtitle,
    chips: chips || [],
    actions: actions
  }), crumbs ? null : /*#__PURE__*/React.createElement(Switcher, {
    items: TABS,
    ariaLabel: "Quotes views"
  }), children);
}
export const Crumb = ({
  to,
  children
}) => /*#__PURE__*/React.createElement(Link, {
  to: to
}, children);